import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  TablePagination
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useApiQuery } from '../../hooks/useApiCache';

const StickyTableCell = styled(TableCell)(({ theme }) => ({
  position: 'sticky',
  left: 0,
  zIndex: 3,
  backgroundColor: theme.palette.background.paper,
  borderRight: `2px solid ${theme.palette.divider}`,
  fontWeight: 600
}));

const EquipesTempoSaidaBaseModal = ({ open, onClose, startDate, endDate, contrato, centro, equipe }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Fetch data with React Query cache
  const { data: rawData, isLoading: loading, error: queryError } = useApiQuery(
    '/tempo-medio-saida-base-equipe-dia',
    { startDate, endDate, contrato, centro, equipe },
    {
      enabled: open && !!startDate && !!endDate,
      staleTime: 5 * 60 * 1000
    }
  );

  const error = queryError?.message || null;

  // Process raw data into equipesData and dias
  const { equipesData, dias } = useMemo(() => {
    const data = Array.isArray(rawData) ? rawData : [];
    if (data.length === 0) return { equipesData: [], dias: [] };

    const equipesMap = {};
    const diasSet = new Set();
    const todasEquipesSet = new Set();

    data.forEach((diaItem) => {
      const dataStr = diaItem.data;
      if (!dataStr) return;
      diasSet.add(dataStr);
      if (Array.isArray(diaItem.equipes)) {
        diaItem.equipes.forEach((eq) => {
          const equipeNome = eq.des_equipe || 'Não informado';
          todasEquipesSet.add(equipeNome);
        });
      }
    });

    todasEquipesSet.forEach((equipeNome) => {
      equipesMap[equipeNome] = {
        nome: equipeNome,
        dias: {},
        somaTempo: 0,
        countTempo: 0,
        mediaTempo: 0
      };
    });

    data.forEach((diaItem) => {
      const dataStr = diaItem.data;
      if (!dataStr) return;

      const equipesDoDia = new Map();
      if (Array.isArray(diaItem.equipes)) {
        diaItem.equipes.forEach((eq) => {
          const equipeNome = eq.des_equipe || 'Não informado';
          equipesDoDia.set(equipeNome, eq);
        });
      }

      todasEquipesSet.forEach((equipeNome) => {
        const equipeItem = equipesDoDia.get(equipeNome);

        if (equipeItem) {
          const tempoValor = typeof equipeItem.tempo_medio_saida_base_minutos === 'number'
            ? equipeItem.tempo_medio_saida_base_minutos
            : (typeof equipeItem.tempo_medio_saida_base_minutos === 'string'
              ? Number(equipeItem.tempo_medio_saida_base_minutos)
              : null);

          equipesMap[equipeNome].dias[dataStr] = Number.isFinite(tempoValor) ? tempoValor : null;

          if (Number.isFinite(tempoValor)) {
            equipesMap[equipeNome].somaTempo += tempoValor;
            equipesMap[equipeNome].countTempo += 1;
          }
        } else {
          equipesMap[equipeNome].dias[dataStr] = null;
        }
      });
    });

    const equipesArray = Object.values(equipesMap).map((e) => ({
      ...e,
      mediaTempo: e.countTempo > 0 ? e.somaTempo / e.countTempo : 0
    }));
    equipesArray.sort((a, b) => a.mediaTempo - b.mediaTempo);

    return { equipesData: equipesArray, dias: Array.from(diasSet).sort() };
  }, [rawData]);

  const formatDateDisplay = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
    } catch {
      return dateStr;
    }
  };

  const getRankColor = (rank) => {
    if (rank === 0) return '#2e7d32';
    if (rank === 1) return '#388e3c';
    if (rank === 2) return '#43a047';
    if (rank < 5) return '#66bb6a';
    if (rank < 10) return '#ff9800';
    return '#757575';
  };

  const getTempoColor = (valor) => {
    if (valor <= 30) return 'success';
    if (valor <= 60) return 'warning';
    return 'error';
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3, maxHeight: '90vh' }
      }}
    >
      <DialogTitle sx={{ m: 0, p: 3, pb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary' }}>
          Tempo Médio de Saída da Base por Equipe
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
          Ranking de equipes por tempo médio de saída da base no período (menor tempo é melhor)
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 2, pb: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
            <Typography color="error">{error}</Typography>
          </Box>
        ) : equipesData.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
            <Typography color="text.secondary">Sem dados para o período selecionado.</Typography>
          </Box>
        ) : (
          <TableContainer
            component={Paper}
            sx={{
              maxHeight: '60vh',
              borderRadius: 2,
              boxShadow: 2
            }}
          >
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <StickyTableCell sx={{ minWidth: 80 }}>Ranking</StickyTableCell>
                  <StickyTableCell sx={{ minWidth: 250 }}>Equipe</StickyTableCell>
                  <StickyTableCell align="center" sx={{ minWidth: 140 }}>
                    Média
                  </StickyTableCell>
                  {dias.map((dia) => (
                    <TableCell
                      key={dia}
                      align="center"
                      sx={{
                        minWidth: 80,
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        borderLeft: '1px solid #e0e0e0'
                      }}
                    >
                      {formatDateDisplay(dia)}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {equipesData
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((equipeRow, idx) => {
                    const globalIndex = page * rowsPerPage + idx;
                    return (
                      <TableRow key={equipeRow.nome} hover>
                        <StickyTableCell>
                          <Chip
                            label={`#${globalIndex + 1}`}
                            size="small"
                            sx={{
                              bgcolor: getRankColor(globalIndex),
                              color: 'white',
                              fontWeight: 700,
                              minWidth: 50
                            }}
                          />
                        </StickyTableCell>
                        <StickyTableCell sx={{ fontWeight: 600 }}>
                          {equipeRow.nome}
                        </StickyTableCell>
                        <TableCell align="center" sx={{ fontWeight: 700 }}>
                          <Chip
                            label={Number.isFinite(equipeRow.mediaTempo) ? Math.round(equipeRow.mediaTempo) : '-'}
                            color={getTempoColor(equipeRow.mediaTempo)}
                            size="small"
                            sx={{ fontWeight: 700, minWidth: 100 }}
                          />
                        </TableCell>
                        {dias.map((dia) => {
                          const tempoDia = equipeRow.dias[dia];
                          const hasValue = Number.isFinite(tempoDia);
                          return (
                            <TableCell
                              key={dia}
                              align="center"
                              sx={{
                                borderLeft: '1px solid #e0e0e0'
                              }}
                            >
                              {!hasValue ? (
                                <Box
                                  sx={{
                                    width: 24,
                                    height: 24,
                                    borderRadius: '50%',
                                    bgcolor: '#e0e0e0',
                                    margin: '0 auto'
                                  }}
                                  title="Sem informação"
                                />
                              ) : (
                                <Chip
                                  label={Math.round(tempoDia)}
                                  color={getTempoColor(tempoDia)}
                                  size="small"
                                  sx={{ fontWeight: 700, fontSize: '0.75rem' }}
                                />
                              )}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={equipesData.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage="Equipes por página:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            />
          </TableContainer>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 2 }}>
        <Button onClick={onClose} variant="outlined" color="primary" sx={{ fontWeight: 600 }}>
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EquipesTempoSaidaBaseModal;
